// Khách hàng: giai đoạn, SLA liên hệ lead mới, bảo vệ khách, chống trùng SĐT, chia lead vòng tròn, gợi ý căn.
import { fold, normPhone } from '@cathouse/contracts';
import type { Rules } from './config.js';
import { DAY, MIN, dateKey, startOfDay } from './dates.js';
import type { EffectiveUnit } from './inventory.js';
import type { ActivityType, Collection, Customer, Id, LeaveSpan, Project, Rating, StageKey, Staff, Timestamp, Unit } from './types.js';
import { values } from './util.js';

export type StageTone = 'info' | 'neutral' | 'brand' | 'warn' | 'good' | 'bad';
export interface StageDef {
  key: StageKey;
  label: string;
  tone: StageTone;
}

/** Phễu khách theo thứ tự hiển thị. */
export const STAGES: readonly StageDef[] = [
  { key: 'new', label: 'Mới', tone: 'info' },
  { key: 'contacted', label: 'Đã liên hệ', tone: 'neutral' },
  { key: 'qualified', label: 'Có nhu cầu', tone: 'brand' },
  { key: 'viewing', label: 'Đã xem dự án', tone: 'brand' },
  { key: 'negotiating', label: 'Đàm phán', tone: 'warn' },
  { key: 'deposited', label: 'Đã cọc', tone: 'good' },
  { key: 'contracted', label: 'Đã ký HĐ', tone: 'good' },
  { key: 'lost', label: 'Thất bại', tone: 'bad' },
];
export const STAGE: Readonly<Record<StageKey, StageDef>> = Object.fromEntries(STAGES.map((s) => [s.key, s])) as Record<StageKey, StageDef>;
export const RATINGS: Readonly<Record<Rating, string>> = { hot: 'Nóng', warm: 'Ấm', cold: 'Lạnh' };
export const ACTIVITY_TYPES: Readonly<Record<ActivityType, string>> = {
  call: 'Gọi điện',
  zalo: 'Nhắn Zalo',
  meet: 'Gặp mặt',
  viewing: 'Dẫn xem dự án',
  quote: 'Gửi báo giá',
  note: 'Ghi chú',
};
export const LOST_REASONS: readonly string[] = ['Không đủ tài chính', 'Mua dự án khác', 'Không liên lạc được', 'Sai thông tin', 'Chưa có nhu cầu', 'Khác'];

// ---------- SLA & bảo vệ khách ----------
export interface SlaState {
  /** Đúng hạn (đã liên hệ trước hạn, hoặc chưa liên hệ nhưng chưa quá hạn). */
  ok: boolean;
  /** Hạn liên hệ = assignedAt + slaMinutes. */
  due: Timestamp;
  /** Đã liên hệ lần đầu. */
  met: boolean;
  /** Chỉ có khi chưa liên hệ: đã quá hạn. */
  overdue?: boolean;
}
export type SlaCustomer = Pick<Customer, 'ownerId' | 'assignedAt'> & Partial<Pick<Customer, 'firstContactAt'>>;

/** SLA liên hệ lead mới: người phụ trách phải liên hệ trong `rules.slaMinutes` kể từ lúc được giao. null = không áp (kho chung). */
export function slaState(c: SlaCustomer, cfg: { rules: Pick<Rules, 'slaMinutes'> }, now: Timestamp = Date.now()): SlaState | null {
  if (!c.ownerId || !c.assignedAt) return null;
  const due = c.assignedAt + cfg.rules.slaMinutes * MIN;
  if (c.firstContactAt) return { ok: c.firstContactAt <= due, due, met: true };
  return { ok: now <= due, due, met: false, overdue: now > due };
}

export type ProtectCustomer = Pick<Customer, 'stage'> & Partial<Pick<Customer, 'lastActivityAt' | 'assignedAt' | 'createdAt'>>;

/**
 * Hạn bảo vệ khách: max(lastActivityAt, assignedAt, createdAt) + protectDays ngày; khách đã cọc / đã ký HĐ
 * được bảo vệ vô thời hạn (Infinity). Hết hạn ⇒ người khác có `customers.create` được nhận.
 */
export function protectedUntil(c: ProtectCustomer, cfg: { rules: Pick<Rules, 'protectDays'> }): number {
  if (c.stage === 'deposited' || c.stage === 'contracted') return Infinity;
  const base = Math.max(c.lastActivityAt || 0, c.assignedAt || 0, c.createdAt || 0);
  return base + cfg.rules.protectDays * DAY;
}

// ---------- Chống trùng ----------
export type PhoneOwner = Pick<Customer, 'id' | 'phone'> & Partial<Pick<Customer, 'phone2'>>;

/** Khách trùng theo SĐT đã chuẩn hoá (khớp số chính hoặc số phụ), bỏ qua `exceptId`. */
export function findDuplicate<C extends PhoneOwner>(customers: Collection<C>, phone: unknown, exceptId?: Id | null): C | null {
  const p = normPhone(phone);
  if (!p) return null;
  return values(customers).find((c) => c.id !== exceptId && (c.phone === p || c.phone2 === p)) || null;
}

// ---------- Chia lead vòng tròn ----------
export type AssigneeCandidate = Pick<Staff, 'id' | 'status'> & Partial<Pick<Staff, 'teamId' | 'receivesLeads'>>;
export type AssignedCustomer = Pick<Customer, 'ownerId' | 'assignedAt'>;

/**
 * Chọn người nhận lead: trong danh sách ứng viên hợp lệ (người gọi đã lọc theo quyền), loại người đã nghỉ việc,
 * bị khoá, đang nghỉ phép đã duyệt hôm nay, khác nhóm `teamId` (nếu có), tắt nhận lead; rồi chọn người nhận ít
 * lead nhất hôm nay (từ 00:00 giờ VN), hoà thì người nhận lead gần nhất lâu nhất. Không ai ⇒ null.
 */
export function pickAssignee<S extends AssigneeCandidate>(
  staffList: readonly S[],
  customers: Collection<AssignedCustomer>,
  leaves: Collection<LeaveSpan>,
  now: Timestamp = Date.now(),
  teamId?: Id | null,
): S | null {
  const today = startOfDay(now);
  const dk = dateKey(now);
  const onLeave = new Set(
    values(leaves)
      .filter((l) => l.status === 'approved' && l.from <= dk && l.to >= dk)
      .map((l) => l.staffId),
  );
  const pool = staffList.filter((s) => s.status !== 'left' && s.status !== 'locked' && !onLeave.has(s.id) && (!teamId || s.teamId === teamId) && s.receivesLeads !== false);
  if (!pool.length) return null;
  const load = new Map<string, number>(),
    last = new Map<string, number>();
  for (const c of values(customers)) {
    if (!c.ownerId || !c.assignedAt) continue;
    if (c.assignedAt >= today) load.set(c.ownerId, (load.get(c.ownerId) || 0) + 1);
    last.set(c.ownerId, Math.max(last.get(c.ownerId) || 0, c.assignedAt));
  }
  pool.sort((a, b) => (load.get(a.id) || 0) - (load.get(b.id) || 0) || (last.get(a.id) || 0) - (last.get(b.id) || 0));
  return pool[0] ?? null;
}

// ---------- Gợi ý căn phù hợp ----------
/** Căn kèm trạng thái hiệu lực (kết quả effectiveUnit) — phần matchUnits cần. */
export type MatchUnit = Pick<Unit, 'projectId' | 'price' | 'type' | 'area'> & Partial<Pick<Unit, 'direction'>> & Pick<EffectiveUnit, 'st'>;
export interface UnitMatch<U> {
  unit: U;
  /** 0–100 */
  score: number;
  why: string[];
}

/**
 * Gợi ý căn đang trống (trạng thái hiệu lực) của dự án đang mở bán theo nhu cầu khách. Điểm: trong ngân sách +40
 * (giá ≤ budgetMax và ≥ 85% budgetMin), không thì ≤ 110% budgetMax +18, vượt nữa ⇒ loại; không có ngân sách +15;
 * đúng loại căn +25; dự án quan tâm +20; diện tích lệch ≤ 15% +10; đúng hướng (không dấu) +5. Sắp điểm giảm dần,
 * hoà thì giá tăng dần; lấy `limit` căn.
 */
export function matchUnits<U extends MatchUnit>(
  customer: Pick<Customer, 'needs'>,
  units: readonly U[],
  projects: Readonly<Record<string, Pick<Project, 'status'>>>,
  limit = 8,
): UnitMatch<U>[] {
  const n = customer.needs || {};
  const out: UnitMatch<U>[] = [];
  for (const u of units) {
    if (u.st !== 'open') continue;
    const p = projects[u.projectId];
    if (!p || p.status !== 'selling') continue;
    let score = 0;
    const why: string[] = [];
    if (n.budgetMax) {
      if (u.price <= n.budgetMax && (!n.budgetMin || u.price >= n.budgetMin * 0.85)) {
        score += 40;
        why.push('Trong ngân sách');
      } else if (u.price <= n.budgetMax * 1.1) {
        score += 18;
        why.push('Vượt ngân sách ≤10%');
      } else continue;
    } else score += 15;
    if (n.unitType && u.type === n.unitType) {
      score += 25;
      why.push(u.type);
    }
    if (n.projectIds && n.projectIds.includes(u.projectId)) {
      score += 20;
      why.push('Dự án quan tâm');
    }
    if (n.area && u.area && Math.abs(u.area - n.area) / n.area <= 0.15) {
      score += 10;
      why.push('Diện tích phù hợp');
    }
    if (n.direction && u.direction && fold(u.direction).includes(fold(n.direction))) {
      score += 5;
      why.push('Hướng ' + u.direction);
    }
    out.push({ unit: u, score: Math.min(100, score), why });
  }
  return out.sort((a, b) => b.score - a.score || a.unit.price - b.unit.price).slice(0, limit);
}
